
import React, { useEffect, useState } from "react";
import sealEndorsed from "../../images/endorsment-seal.png";
import sealYellowBlank from "../../images/default-candidate.png";
import sealYellowConcern from "../../images/endorsment1.png";

/** YES=green endorsed; NO=yellow concerned; blank=neutral yellow */
function getStampForCandidate(a4hValue) {
  const v = String(a4hValue || "").trim().toUpperCase();
  if (v === "YES") {
    return { src: sealEndorsed, alt: "A4H Endorsed", tone: "endorsed" };
  }
  if (v === "NO") {
    return { src: sealYellowConcern, alt: "A4H Concerned", tone: "concern" };
  }
  return { src: sealYellowBlank, alt: "A4H", tone: "blank" };
}

export default function VoterGuide() {
  const [activeTab, setActiveTab] = useState("myzip");
  const [activeFilter, setActiveFilter] = useState("All");
  const [showGuide, setShowGuide] = useState(true);

  const [zipInput, setZipInput] = useState("");
  const [selectedState, setSelectedState] = useState("");

  const [results, setResults] = useState([]);
  const [endorsedResults, setEndorsedResults] = useState([]);
  const [stateResults, setStateResults] = useState([]);

  const [loading, setLoading] = useState(false);

  const [DATA, setDATA] = useState({
    candidates: [],
    zipCodes: [],
    splitZips: new Set(),
    stateConfig: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);

      const spreadsheetId =
        "1ot939X173RXXuAk7Zs66OD_3sc9t8a9HcofS3Fv11Pg";
      const baseUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/export?format=csv&gid=`;

      const urls = {
        usSenate: baseUrl + "1588428220",
        usHouse: baseUrl + "574891025",
        governor: baseUrl + "106542971",
        stateSenate: baseUrl + "755731723",
        stateHouse: baseUrl + "2114817971",
        stateBoard: baseUrl + "153617938",
        localCounty: baseUrl + "983206650",
        localCity: baseUrl + "1257794290",
        localJudges: baseUrl + "324526209",
        localOther: baseUrl + "151782435",
        zipCodes: baseUrl + "1136776422",
        stateConfig: baseUrl + "306598189",
      };

      const fetchSheet = async (url) => {
        const res = await fetch(url);
        const text = await res.text();

        const rows = [];
        let currentRow = [];
        let currentField = "";
        let insideQuotes = false;

        for (let i = 0; i < text.length; i++) {
          const char = text[i];
          const nextChar = text[i + 1];

          if (char === '"') {
            if (insideQuotes && nextChar === '"') {
              currentField += '"';
              i++;
            } else {
              insideQuotes = !insideQuotes;
            }
          } else if (char === "," && !insideQuotes) {
            currentRow.push(currentField.trim());
            currentField = "";
          } else if (char === "\n" && !insideQuotes) {
            currentRow.push(currentField.trim());

            if (currentRow.some((f) => f)) {
              rows.push(currentRow);
            }

            currentRow = [];
            currentField = "";
          } else {
            currentField += char;
          }
        }

        if (currentField || currentRow.length) {
          currentRow.push(currentField.trim());

          if (currentRow.some((f) => f)) {
            rows.push(currentRow);
          }
        }

        const [headers = [], ...dataRows] = rows;

        return { headers, rows: dataRows };
      };

      const normalizeHeader = (value) =>
        String(value || "")
          .replace(/^\uFEFF/, "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");

      const getColumnIndex = (headers, columnName) => {
        const normalizedColumn = normalizeHeader(columnName);

        return headers.findIndex(
          (header) => normalizeHeader(header) === normalizedColumn
        );
      };

      const [
        usSenate,
        usHouse,
        governor,
        stateSenate,
        stateHouse,
        stateBoard,
        localCounty,
        localCity,
        localJudges,
        localOther,
        zipCodes,
        stateConfig,
      ] = await Promise.all([
        fetchSheet(urls.usSenate),
        fetchSheet(urls.usHouse),
        fetchSheet(urls.governor),
        fetchSheet(urls.stateSenate),
        fetchSheet(urls.stateHouse),
        fetchSheet(urls.stateBoard),
        fetchSheet(urls.localCounty),
        fetchSheet(urls.localCity),
        fetchSheet(urls.localJudges),
        fetchSheet(urls.localOther),
        fetchSheet(urls.zipCodes),
        fetchSheet(urls.stateConfig),
      ]);

      const processCandidate = (row, office, headers) => {
        const onBallotIndex = getColumnIndex(headers, "on_ballot_2026");
        const wonLostIndex = getColumnIndex(headers, "won_lost");

        return {
          state: row[0],
          district: row[1],
          raceName: row[2],
          name: row[3],
          party: row[4],
          status: row[5],
          primaryDate: row[6],
          filingStatus: row[7],
          a4h: row[8],
          zipCodes: row[9],
          fecId: row[10],
          url: row[11],
          // M is the existing "on ballot" column and V is "Won / Lost".
          // Prefer the headers so this remains reliable if columns are moved.
          onBallot2026:
            onBallotIndex >= 0 ? row[onBallotIndex] : row[12],
          electionResult:
            wonLostIndex >= 0 ? row[wonLostIndex] : row[21],
          // W and X hold the candidate's total votes and vote percentage.
          voteCount: row[22],
          votePercentage: row[23],
          office,
        };
      };

      const candidates = [
        ...usSenate.rows.map((r) =>
          processCandidate(r, "US Senate", usSenate.headers)
        ),
        ...usHouse.rows.map((r) =>
          processCandidate(r, "US House", usHouse.headers)
        ),
        ...governor.rows.map((r) =>
          processCandidate(r, "Governor", governor.headers)
        ),
        ...stateSenate.rows.map((r) =>
          processCandidate(r, "State Senate", stateSenate.headers)
        ),
        ...stateHouse.rows.map((r) =>
          processCandidate(r, "State House", stateHouse.headers)
        ),
        ...stateBoard.rows.map((r) =>
          processCandidate(r, "State Board", stateBoard.headers)
        ),
        ...localCounty.rows.map((r) =>
          processCandidate(r, "Local - County", localCounty.headers)
        ),
        ...localCity.rows.map((r) =>
          processCandidate(r, "Local - City", localCity.headers)
        ),
        ...localJudges.rows.map((r) =>
          processCandidate(r, "Local - Judges", localJudges.headers)
        ),
        ...localOther.rows.map((r) =>
          processCandidate(r, "Local - Other", localOther.headers)
        ),
      ].filter((c) => {
        const onBallot = String(c.onBallot2026 || "").trim().toUpperCase();
        const electionResult = String(c.electionResult || "")
          .trim()
          .toUpperCase();
        const hasElectionResult = electionResult === "W" || electionResult === "L";

        // Show candidates marked Y or blank in M. A recorded result in V must
        // always remain visible, including candidates marked N in M.
        const shouldShow = onBallot === "Y" || onBallot === "" || hasElectionResult;

        return c.state && c.district && c.name && shouldShow;
      });

      const zipData = zipCodes.rows.map((r) => ({
        state: r[0],
        office: r[1],
        district: r[2],
        zip: r[3],
        isSplit: r[5] === "Yes",
      }));

      const splitZips = new Set(
        zipData.filter((z) => z.isSplit).map((z) => z.zip)
      );

      const states = stateConfig.rows.map((r) => ({
        state: r[1]?.trim().toUpperCase(),
        stateName: r[2]?.trim(),
        hasLocalRaces:
          String(r[3] || "").trim().toUpperCase() === "YES",
      }));

      setDATA({
        candidates,
        zipCodes: zipData,
        splitZips,
        stateConfig: states,
      });

      setEndorsedResults(
        candidates.filter(
          (c) => String(c.a4h || "").toUpperCase() === "YES"
        )
      );

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  }

  function getPartyClass(party) {
    if (!party) return "party-other";

    const p = party.toLowerCase();

    if (p.includes("dem")) return "party-dem";
    if (p.includes("rep")) return "party-rep";
    if (p.includes("ind")) return "party-ind";

    return "party-other";
  }

  function getInitials(name) {
    return (name || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();
  }

  function getOfficeLabel(office, state) {
    const officeMap = {
      "US Senate": "US Senate",
      "US House": "US House",
      Governor: "Governor",
      "State Senate": `${state} Senate`,
      "State House": `${state} House`,
      "State Board": `${state} Board`,
      "Local - County": "County",
      "Local - City": "City",
      "Local - Judges": "Judicial",
      "Local - Other": "Local",
    };

    return officeMap[office] || office;
  }

  function searchZip() {
    if (!/^\d{5}$/.test(zipInput)) {
      alert("Please enter valid ZIP");
      return;
    }

    const zipMatches = DATA.zipCodes.filter(
      (z) => z.zip === zipInput
    );

    if (!zipMatches.length) {
      setResults([]);
      return;
    }

    const zipState = zipMatches[0].state;

    const found = DATA.candidates.filter((c) => {
      if (c.state !== zipState) return false;

      const relevantMappings = zipMatches.filter(
        (z) =>
          z.office === c.office &&
          z.state === c.state
      );

      if (relevantMappings.length > 0) {
        const matched = relevantMappings.some(
          (z) => z.district === c.district
        );

        if (matched) return true;
      }

      if (!c.zipCodes || c.zipCodes.trim() === "") {
        return true;
      }

      return String(c.zipCodes).includes(zipInput);
    });

    setResults(found);
  }

  function showStateResults(state) {
    setSelectedState(state);

    if (!state) {
      setStateResults([]);
      return;
    }

    const stateCandidates = DATA.candidates.filter(
      (c) => c.state === state
    );

    setStateResults(stateCandidates);
  }

  function getFilteredResults(baseResults) {
    if (activeFilter === "All") return baseResults;

    if (activeFilter === "Local") {
      return baseResults.filter((c) =>
        c.office.startsWith("Local")
      );
    }

    return baseResults.filter(
      (c) => c.office === activeFilter
    );
  }

  function groupCandidates(candidates) {
    const officeOrder = [
      "Governor",
      "US Senate",
      "US House",
      "State Senate",
      "State House",
      "State Board",
      "Local - County",
      "Local - City",
      "Local - Judges",
      "Local - Other",
    ];

    const groups = {};

    officeOrder.forEach((o) => (groups[o] = []));

    candidates.forEach((c) => {
      if (groups[c.office]) {
        groups[c.office].push(c);
      }
    });

    return { groups, officeOrder };
  }

  function renderCandidates(candidateList) {
    const filtered = getFilteredResults(candidateList);
    const iconMap = {
      "Governor": "👑",
      "US Senate": "🏛️",
      "US House": "📜",
      "State Senate": "⚖️",
      "State House": "🏢",
      "State Board": "🗳️",
      "Local - County": "🌆",
      "Local - City": "🏙️",
      "Local - Judges": "👨‍⚖️",
      "Local - Other": "⭐",
    };
    if (!filtered.length) {
      return (
        <div className="prompt-box">
          No candidates found.
        </div>
      );
    }

    const { groups, officeOrder } =
      groupCandidates(filtered);

    return officeOrder.map((office) => {
      if (!groups[office].length) return null;

      const labelMap = {
        "US Senate": "U.S. Senate",
        "US House": "U.S. House of Representatives",
        Governor: "Governor",
        "State Senate": "State Senate",
        "State House": "State House",
        "State Board": "State Board",
        "Local - County": "County Races",
        "Local - City": "City Races",
        "Local - Judges": "Judicial Races",
        "Local - Other": "Other Local Races",
      };

      return (
        <div className="race-group" key={office}>
          <div className="race-header">
            <span className="race-header-icon">
              {iconMap[office]}
            </span>

            <span className="race-header-text">
              {labelMap[office]}
            </span>
          </div>

          {groups[office].map((c, i) => {
            const isEndorsed =
              String(c.a4h).toUpperCase() ===
              "YES";

            const isConcern =
              String(c.a4h).toUpperCase() ===
              "NO";

            const stamp = getStampForCandidate(c.a4h);
            const electionResult = String(c.electionResult || "")
              .trim()
              .toUpperCase();
            const resultLabel =
              electionResult === "W"
                ? "Won"
                : electionResult === "L"
                ? "Lost"
                : null;
            const resultThumb =
              electionResult === "W"
                ? "👍"
                : electionResult === "L"
                ? "👎"
                : null;

            return (
              <div
                key={i}
                className={`candidate-card ${
                  isEndorsed
                    ? "endorsed"
                    : isConcern
                    ? "concern"
                    : "neutral"
                }`}
              >
                {resultLabel && (
                  <span
                    className={`election-result-ribbon election-result-ribbon--${electionResult}`}
                  >
                    {resultLabel}
                  </span>
                )}
                <div className="candidate-info">
                  <div className="candidate-name">
                    {c.url ? (
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="candidate-name-link"
                      >
                        {c.name}
                      </a>
                    ) : (
                      c.name
                    )}
                  </div>

                  {c.url && (
                    <div className="candidate-website">
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {c.url}
                      </a>
                    </div>
                  )}

                  <div className="candidate-meta">
                    <span
                      className={`party-badge ${getPartyClass(
                        c.party
                      )}`}
                    >
                      {c.party || "Party TBD"}
                    </span>

                    {c.status && (
                      <span className="status-badge">
                        {c.status}
                      </span>
                    )}

                    <span className="state-badge">
                      {c.state}
                    </span>

                    <span className="district-badge">
                      {(c.office.startsWith(
                        "Local -"
                      ) ||
                        c.office ===
                          "State Board") &&
                      c.raceName
                        ? c.raceName
                        : c.district}
                    </span>

                    {!c.office.startsWith(
                      "Local -"
                    ) &&
                      c.office !==
                        "State Board" && (
                        <span className="office-badge">
                          {getOfficeLabel(
                            c.office,
                            c.state
                          )}
                        </span>
                      )}
                  </div>

                  {c.primaryDate && (
                    <div className="candidate-date">
                      Primary:
                      <span>
                        {" "}
                        {c.primaryDate}
                      </span>
                    </div>
                  )}

                  {(c.voteCount || c.votePercentage || resultThumb) && (
                    <div className="candidate-results" aria-label="Election results">
                      {c.voteCount && (
                        <span>
                          <strong>Votes:</strong> {c.voteCount}
                        </span>
                      )}
                      {c.votePercentage && (
                        <>
                          <span className="vote-share">
                            <strong>Vote share:</strong> {c.votePercentage}
                          </span>
                        </>
                      )}
                      {resultThumb && (
                        <span
                          className="vote-result-thumb"
                          aria-label={electionResult === "W" ? "Won" : "Lost"}
                          role="img"
                        >
                          {resultThumb}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <img
                  src={stamp.src}
                  alt={stamp.alt}
                  className={`endorsement-seal endorsement-seal--${stamp.tone}`}
                />
              </div>
            );
          })}
        </div>
      );
    });
  }

  return (
    <div className="voter-guide-page">
      <style>{`
.voter-guide-page,
.voter-guide-page * {
  box-sizing: border-box;
}

.voter-guide-page {
  margin: 0;
  font-family: Arial, sans-serif;
  background: linear-gradient(to right, #ffdb5e 0%, #ffffff 100%);
  color: #1a1a1a;
}


.hero{
  background: linear-gradient(to right, #ffdb5e 0%, #ffffff 100%);
  color:#fff;
  text-align:center;
  padding:4rem 1.25rem 3.5rem;
  position:relative;
}

.voter-guide-page .hero h1{
  font-size:48px;
  font-weight:900;
  margin:0 0 14px;
  line-height:1.2;
}

.subtitle{
  color:#1a2a5a;
  font-size:clamp(18px,3vw,30px);
  font-weight:700;
  max-width:980px;
  margin:0 auto;
  line-height:1.35;
}

.intro-section{
  background: linear-gradient(to right, #ffdb5e 0%, #ffffff 100%);
  border-bottom:none;
  padding:1.5rem 1.25rem;
}

.intro-inner{
  max-width:1200px;
  margin:0 auto;
}
.section-divider{
  width:100%;
  max-width:900px;
  margin:32px auto 38px;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:18px;
}

.section-divider::before,
.section-divider::after{
  content:"";
  flex:1;
  height:3px;

  background: linear-gradient(
    to right,
    transparent,
    #1a2a5a,
    #d4af37,
    transparent
  );

  border-radius:10px;

  box-shadow:
    0 2px 6px rgba(0,0,0,0.18);
}

.section-divider span{
  font-size:26px;
  color:#1a2a5a;
  text-shadow:0 2px 6px rgba(0,0,0,0.15);

  animation: dividerGlow 2.5s ease-in-out infinite;
}

@keyframes dividerGlow{
  0%{ transform:scale(1); opacity:.8; }
  50%{ transform:scale(1.15); opacity:1; }
  100%{ transform:scale(1); opacity:.8; }
}
.intro-section h3{
  font-size:32px;
  font-weight:900;
  color:#1a2a5a;
  margin-bottom:20px;
}

.intro-section p{
  font-size:28px;
  color:#444;
  line-height:1.9;
  font-weight:500;
}

.intro-section li{
  font-size:22px;
  color:#444;
  line-height:1.8;
  font-weight:500;
}

.intro-section ul{
  padding-left:35px;
  margin-top:20px;
}



.tab-navigation{
  

  display:flex;
  justify-content:center;
  gap:28px;

  padding:18px 20px;
}

.tab-btn{
  position: relative;

  color:#fff;
  border:none;

  padding:18px 38px;
  min-width:190px;

  border-radius:16px;

  font-size:22px;
  font-weight:800;

  cursor:pointer;

  display:flex;
  align-items:center;
  justify-content:center;
  gap:12px;

  transition: all 0.18s ease;

  box-shadow:
    0 6px 0 rgba(120,60,0,0.9),
    0 10px 22px rgba(0,0,0,0.28),
    inset 0 2px 0 rgba(255,255,255,0.45),
    inset 0 -4px 10px rgba(0,0,0,0.22);
}

.tab-btn::before{
  content:"";
  position:absolute;
  top:3px;
  left:6px;
  right:6px;
  height:45%;

  border-radius:12px;

  background: linear-gradient(
    to bottom,
    rgba(255,255,255,0.55),
    rgba(255,255,255,0.05)
  );

  pointer-events:none;
}

/* hover effect */
.tab-btn:hover{
  transform: translateY(-3px);

  box-shadow:
    0 8px 0 rgba(120,60,0,0.9),
    0 14px 26px rgba(0,0,0,0.32),
    inset 0 2px 0 rgba(255,255,255,0.45),
    inset 0 -4px 10px rgba(0,0,0,0.22);
}

/* click effect */
.tab-btn:active{
  transform: translateY(4px);

  box-shadow:
    0 2px 0 rgba(120,60,0,0.9),
    0 4px 10px rgba(0,0,0,0.25);
}

/* active button */
.tab-btn.active{
  filter: brightness(1.08);
  transform: scale(1.02);
}
/* BUTTON 1 — Deep Orange */
.tab-btn:nth-child(1){
  background: linear-gradient(
    to bottom,
    #ec5f5fff 0%,
    #762f2fff 50%,
    #773131ff 100%
  );

  border:2px solid #ffc76b;
}

/* BUTTON 2 — Golden Orange */
.tab-btn:nth-child(2){
  background: linear-gradient(
    to bottom,
    #ec5f5fff 0%,
    #762f2fff 50%,
    #773131ff 100%
  );

  border:2px solid #ffd98f;
}

/* BUTTON 3 — Soft Peach Orange */
.tab-btn:nth-child(3){
  background: linear-gradient(
    to bottom,
    #ec5f5fff 0%,
    #762f2fff 50%,
    #773131ff 100%
  );

  border:2px solid #ffe1bf;
}
.search-section,
.state-selector-section{
  background: linear-gradient(to right, #ffdb5e 0%, #ffffff 100%);
  border-bottom:none;
  padding:1.5rem 1.25rem;
}

.search-inner,
.state-selector-inner{
  max-width:680px;
  margin:0 auto;
  text-align:center;
}

.search-inner h2,
.state-selector-inner h2{
  font-size:40px;
  font-weight:900;
  color:#1a2a5a;
}

.search-inner p,
.state-selector-inner p{
  margin-top:12px;
  margin-bottom:24px;
  color:#666;
  font-size:24px;
}

.search-row,
.state-selector-row{
  display:flex;
  gap:8px;
  justify-content:center;
  flex-wrap:wrap;
}


.state-selector-row select{
  flex:1;
  min-width:320px;
  max-width:500px;
  padding:18px 22px;
  border:2px solid #c0cce0;
  border-radius:10px;
  font-size:22px;
}
.search-row input{
  flex:1;
  min-width:320px;
  max-width:500px;
  padding:18px 22px;
  border:2px solid #c0cce0;
  border-radius:10px;
  font-size:22px;
}
  
.search-row button{
  background: linear-gradient(
    to bottom,
    #ec5f5fff 0%,
    #762f2fff 50%,
    #773131ff 100%
  );

  color:#fff;
  border:2px solid #ffd27a;

  padding:18px 36px;
  border-radius:12px;

  font-size:20px;
  font-weight:800;

  cursor:pointer;

  box-shadow:
    0 5px 0 #8a3c00,
    0 8px 18px rgba(0,0,0,0.25),
    inset 0 2px 0 rgba(255,255,255,0.4),
    inset 0 -4px 8px rgba(0,0,0,0.2);

  transition: all 0.18s ease;
}

.search-row button:hover{
  transform: translateY(-2px);

  box-shadow:
    0 7px 0 #8a3c00,
    0 12px 22px rgba(0,0,0,0.3),
    inset 0 2px 0 rgba(255,255,255,0.4),
    inset 0 -4px 8px rgba(0,0,0,0.2);
}

.search-row button:active{
  transform: translateY(3px);

  box-shadow:
    0 2px 0 #8a3c00,
    0 4px 10px rgba(0,0,0,0.25);
}

.filter-bar{
  background: linear-gradient(to right, #ffdb5e 0%, #ffffff 100%);

  border-bottom:none;

  padding:1.4rem 1.25rem;

  display:flex;
  gap:22px;

  flex-wrap:wrap;
  justify-content:center;
  align-items:center;
}

.filter-pill{
  position: relative;

  background: linear-gradient(
    to bottom,
    #ffffff 0%,
    #f2f2f2 45%,
    #dddddd 100%
  );

  color:#1a2a5a;

  border:2px solid #b9c2d3;

  padding:14px 30px;

  border-radius:22px;

  font-size:20px;
  font-weight:900;

  cursor:pointer;

  min-width:150px;

  box-shadow:
    0 5px 0 #c89d2b,
    0 8px 16px rgba(0,0,0,0.18),
    inset 0 2px 0 rgba(255,255,255,0.95),
    inset 0 -3px 6px rgba(0,0,0,0.08);

  transition: all 0.15s ease;
}

/* glossy top shine */
.filter-pill::before{
  content:"";

  position:absolute;

  top:3px;
  left:5px;
  right:5px;

  height:42%;

  border-radius:18px;

  background: linear-gradient(
    to bottom,
    rgba(255,255,255,0.95),
    rgba(255,255,255,0.1)
  );

  pointer-events:none;
}

/* hover lift */
.filter-pill:hover{
  transform: translateY(-2px);

  box-shadow:
    0 7px 0 #c89d2b,
    0 12px 20px rgba(0,0,0,0.22),
    inset 0 2px 0 rgba(255,255,255,0.95),
    inset 0 -3px 6px rgba(0,0,0,0.08);
}

/* pressed effect */
.filter-pill:active{
  transform: translateY(4px);

  box-shadow:
    0 2px 0 #c89d2b,
    0 4px 8px rgba(0,0,0,0.18),
    inset 0 2px 4px rgba(0,0,0,0.12);
}

/* active selected button */
.filter-pill.active{
  background: linear-gradient(
    to bottom,
    #ec5f5fff 0%,
    #762f2fff 50%,
    #773131ff 100%
  );

  color:#fff;

  border:2px solid #d95555ff;

  box-shadow:
    0 5px 0 #3b2a12,
    0 10px 18px rgba(0,0,0,0.3),
    inset 0 2px 0 rgba(255,255,255,0.35),
    inset 0 -4px 8px rgba(0,0,0,0.25);
}

.results-section{
  
  max-width:1300px;
  margin:0 auto;
  padding:2.5rem 2rem;
}

.race-group{
  margin-bottom:2rem;
}

.race-header{
  position: relative;

  width: 82%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;

  margin: 0 auto 24px auto;
  padding: 20px 30px;

  border-radius: 18px;

  background:
    linear-gradient(
      135deg,
      #7a0000 0%,
      #c1121f 22%,
      #ff3b3b 48%,
      #d90429 72%,
      #5a0000 100%
    );

  color: #ffffff;

  font-size: 24px;
  font-weight: 700;
  letter-spacing: .14em;
  text-transform: uppercase;

  overflow: hidden;

  border: 2px solid rgba(255,255,255,0.28);

  box-shadow:
    0 10px 24px rgba(0,0,0,0.28),
    inset 0 2px 0 rgba(255,255,255,0.45),
    inset 0 -6px 12px rgba(0,0,0,0.24);
}

/* glossy shine */
.race-header::before{
  content:"";

  position:absolute;
  top:4px;
  left:8px;
  right:8px;

  height:48%;

  border-radius:14px;

  background: linear-gradient(
    to bottom,
    rgba(255,255,255,0.52),
    rgba(255,255,255,0.04)
  );
}

/* moving gold shine */
.race-header::after{
  content:"";

  position:absolute;
  inset:-40%;

  background:
    linear-gradient(
      120deg,
      transparent 20%,
      rgba(255,215,0,0.28) 50%,
      transparent 80%
    );

  transform: rotate(8deg);

  animation: premiumShine 4s linear infinite;
}
.race-header-icon{
  position: relative;
  z-index: 2;

  display:flex;
  align-items:center;
  justify-content:center;

  font-size: 28px;

  margin-right: 14px;

  filter:
    drop-shadow(0 2px 4px rgba(0,0,0,0.35));
}

.race-header-text{
  position: relative;
  z-index: 2;
}
@keyframes premiumShine{
  0%{
    transform: translateX(-120%) rotate(8deg);
  }

  100%{
    transform: translateX(120%) rotate(8deg);
  }
}

.candidate-card{
  background:#fff;
  border:1px solid #e0e8f0;
  border-radius:14px;
  padding:1rem 1.4rem;
  margin:0 auto 14px auto;
  display:flex;
  gap:1rem;
  align-items:center;
  box-shadow:0 3px 8px rgba(0,0,0,.05);

  width:82%;
  min-height:120px;
  position:relative;
  overflow:hidden;
}

.election-result-ribbon {
  position: absolute;
  top: 13px;
  right: -38px;
  width: 132px;
  padding: 5px 0;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-align: center;
  text-transform: uppercase;
  transform: rotate(45deg);
  box-shadow: 0 2px 5px rgba(0,0,0,.18);
  z-index: 5;
}

/* PREMIUM WON RIBBON */

/* =========================================
   PREMIUM WAVY EMERALD WON RIBBON
========================================= */

.election-result-ribbon--W {
  position: absolute;

  top: 3px;
  right: -54px;

  width: 195px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 0 14px;

  transform: rotate(35deg);
  transform-origin: center;

  color: #fff4a8;

  /* Rich emerald fabric with curved lighting */
  background:
    radial-gradient(
      ellipse at 50% 0%,
      rgba(110,255,130,.45),
      transparent 65%
    ),
    linear-gradient(
      180deg,
      #034016 0%,
      #087b29 18%,
      #13b83e 38%,
      #07852d 58%,
      #034016 100%
    );

  /* Pointed, folded tails at the lower end of the award ribbon. */
  border-radius: 50% 18% 45% 18% / 22% 15% 22% 15%;

  border-top: 2px solid #fff1a8;
  border-bottom: 3px solid #d5a82e;

  font-size: 15px;
  font-weight: 950;
  letter-spacing: .16em;

  text-shadow:
    0 2px 3px rgba(0,0,0,.75),
    0 0 8px rgba(255,255,180,.5);

  box-shadow:
    0 7px 15px rgba(0,45,15,.55),
    0 0 12px rgba(255,215,0,.4),
    inset 0 3px 5px rgba(255,255,255,.45),
    inset 0 -7px 9px rgba(0,30,10,.55);

  z-index: 5;
  isolation: isolate;
  overflow: hidden;
}

/* GOLDEN LAUREL ORNAMENTS */

.ribbon-laurel {
  position: relative;
  z-index: 4;

  color: #ffdf65;

  font-family: Georgia, serif;
  font-size: 23px;
  font-weight: 900;

  line-height: 1;

  text-shadow:
    0 1px 2px #664600,
    0 0 6px rgba(255,220,80,.8);
}

.ribbon-laurel--left {
  transform: rotate(-35deg);
}

.ribbon-laurel--right {
  transform: rotate(145deg);
}

/* GOLDEN FABRIC EDGES */

.election-result-ribbon--W::before {
  content: "";

  position: absolute;
  inset: 4px 0;

  border-top: 1px solid rgba(255,245,180,.7);
  border-bottom: 1px solid rgba(255,220,100,.6);

  border-radius: inherit;

  background:
    linear-gradient(
      180deg,
      rgba(255,255,255,.17),
      transparent 45%,
      rgba(0,0,0,.12)
    );

  pointer-events: none;
  z-index: 1;
}

/* MOVING GOLDEN SHINE */

.election-result-ribbon--W::after {
  content: "";

  position: absolute;

  top: -60%;
  left: -80%;

  width: 35%;
  height: 220%;

  background: linear-gradient(
    90deg,
    transparent,
    rgba(255,255,255,.75),
    rgba(255,225,130,.5),
    transparent
  );

  transform: skewX(-25deg);

  animation: premiumRibbonShine 3.5s ease-in-out infinite;

  pointer-events: none;
  z-index: 3;
}

@keyframes premiumRibbonShine {
  0% {
    left: -80%;
    opacity: 0;
  }

  20% {
    opacity: 1;
  }

  55% {
    left: 150%;
    opacity: 1;
  }

  70%, 100% {
    left: 150%;
    opacity: 0;
  }
}

/* PREMIUM HOVER EFFECT */

.candidate-card:has(.election-result-ribbon--W):hover
.election-result-ribbon--W {

  filter: brightness(1.2) saturate(1.2);

  box-shadow:
    0 9px 20px rgba(0,70,20,.7),
    0 0 18px rgba(255,215,0,.65),
    inset 0 3px 5px rgba(255,255,255,.6),
    inset 0 -7px 9px rgba(0,30,10,.5);

  transition: all .35s ease;
}


.election-result-ribbon--L{
  background:#b42318;
}

.candidate-card.endorsed{
  border-left:6px solid #d4af37;
  background:linear-gradient(180deg,#fffdf5 0%,#fff7e6 100%);
}

.candidate-card.concern{
  background:linear-gradient(135deg,#fffdf0 0%,#fff7d6 100%);
  border:2px solid #e8c547;
}

.candidate-card.neutral{
  background:linear-gradient(135deg,#fffef8 0%,#fff9e8 100%);
  border:1px solid #f0e0a0;
}



.candidate-info{
  flex:1;
}

.candidate-name{
  font-size:28px;
  font-weight:900;
  color:#1a2a5a;
  margin-bottom:4px;
  line-height:1;
}

.candidate-name-link{
  color:#1a2a5a;
  text-decoration:none;
  font-size:28px;
  font-weight:900;
  line-height:1;
}

.candidate-name-link:hover{
  color:#5b9cf6;
}

.candidate-meta{
  display:flex;
  gap:12px;
  flex-wrap:wrap;
  margin-top:18px;
}

.candidate-results{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:12px;
}

.candidate-results span{
  padding:6px 10px;
  border-radius:8px;
  background:#eef4f8;
  color:#1a2a5a;
  font-size:14px;
}

.candidate-results .vote-share{
}

.candidate-results .vote-result-thumb{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:auto;
  height:auto;
  padding:0;
  border-radius:0;
  background:transparent;
  font-size:26px;
  line-height:1;
  box-shadow:none;
}

.party-badge,
.district-badge,
.state-badge,
.office-badge{
  font-size:16px;
  font-weight:800;
  padding:8px 16px;
  border-radius:20px;
}

.party-dem{
  background:#bfdbfe;
  color:#1e3a8a;
}

.party-rep{
  background:#fee2e2;
  color:#991b1b;
}

.party-ind{
  background:#e5e7eb;
}

.party-other{
  background:#f3f4f6;
}

.district-badge{
  background:#f0f4ff;
  color:#3b5bdb;
}

.state-badge{
  background:#f0fdf4;
  color:#166534;
}

.office-badge{
  background:#f3f4f6;
  color:#4b5563;
}
.status-badge{
  background:#fff7ed;
  color:#9a3412;
  font-size:16px;
  font-weight:800;
  padding:8px 16px;
  border-radius:20px;
}
.candidate-avatar{
  width:72px;
  height:72px;
  border-radius:50%;
  background:#1a2a5a;
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:22px;
  font-weight:900;
}

.endorsement-seal{
  width:72px !important;
  height:72px !important;
  max-width:72px !important;
  max-height:72px !important;
  object-fit:contain !important;
  object-position:center !important;
  flex-shrink:0 !important;
  align-self:center;
}

.endorsement-seal--endorsed,
.endorsement-seal--concern,
.endorsement-seal--blank,
.endorsement-seal--yellow{
  width:72px !important;
  height:72px !important;
}

.prompt-box{
  background: linear-gradient(to right, #ffdb5e 0%, #ffffff 100%);
  border:1px dashed #c0cce0;
  border-radius:8px;
  padding:2rem 1.5rem;
  text-align:center;
}

.data-source{
  font-size:11px;
  color:#aaa;
  text-align:center;
  margin-top:20px;
}
.tab-icon{
  display:flex;
  align-items:center;
  justify-content:center;

  width:34px;
  height:34px;

  font-size:22px;
  font-weight:900;

  color:white;

  text-shadow:
    0 2px 4px rgba(0,0,0,0.4);

  position:relative;
  z-index:2;
}

/* ZIP square dotted icon */
.zip-icon{
  font-size:26px;
  letter-spacing:-2px;
}
.young-voters-section{
  width:100%;
  max-width:1400px;

  margin:60px auto 0;

  padding:50px 60px;

  

  border-radius:28px;

  border:2px solid rgba(212,175,55,0.35);

  box-shadow:
    0 12px 35px rgba(0,0,0,0.12);

  text-align:left;
}

.young-voters-section h2{
  font-size:38px;
  line-height:1.3;
  font-weight:900;
  color:#7a0000;
  margin-bottom:24px;
}

.young-voters-section p{
  font-size:22px;
  line-height:1.9;
  color:#333;
  margin-bottom:26px;
}

.yv-step{
  margin-top:34px;
}

.yv-step h3{
  font-size:28px;
  font-weight:900;
  color:#1a2a5a;
  margin-bottom:18px;
}

.yv-step ul{
  padding-left:28px;
}

.yv-step li{
  font-size:21px;
  line-height:1.9;
  color:#444;
  margin-bottom:10px;
}

.yv-buttons{
  display:flex;
  flex-wrap:wrap;
  gap:18px;
  margin-top:24px;
}

.yv-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;

  padding:16px 28px;

  background:
    linear-gradient(
      to bottom,
      #0e06ffff 0%,
      #051083ff 55%,
      #03042dff 100%
    );

  color:#fff;
  text-decoration:none;

  border-radius:14px;

  font-size:18px;
  font-weight:800;

  transition:all .2s ease;

  box-shadow:
    0 6px 14px rgba(0,0,0,0.22);

  border:2px solid #ffd27a;
}

.yv-btn:hover{
  color:#fff;
  transform:translateY(-3px) scale(1.02);
}

.status-btn{
  width:100%;
  max-width:420px;
}

.yv-notice{
  margin-top:36px;

  padding:24px;

  background:#fff7e6;

  border-left:6px solid #d4af37;

  border-radius:14px;

  font-size:18px;
  line-height:1.8;
  color:#444;
}
.young-voters-wrapper{
  width:100vw;

  margin-left:calc(50% - 50vw);
  margin-right:calc(50% - 50vw);

  padding:0 40px;

  display:flex;
  justify-content:center;
}
.candidate-photo{
  width:90px;
  height:90px;
  border-radius:50%;
  object-fit:cover;
  border:3px solid #d4af37;
  box-shadow:0 4px 10px rgba(0,0,0,.15);
  flex-shrink:0;
}
/* =========================
   PERFECT MOBILE VERSION
   ========================= */
@media (max-width: 768px) {

  /* HERO */
  .voter-guide-page .hero{
    padding: 1.25rem 1rem 1.5rem;
  }

  .voter-guide-page .hero h1{
    font-size: 20px;
    line-height: 1.25;
    margin: 0 0 8px;
  }

  .subtitle{
    font-size: 15px;
    line-height: 1.5;
    padding: 0 8px;
  }

  .section-divider{
    margin: 14px auto 18px;
  }

  .section-divider span{
    font-size: 18px;
  }

  /* INTRO */
  .intro-section{
    padding: 1rem;
  }

  .intro-section h3{
    font-size: 16px;
    margin-bottom: 12px;
  }

  .intro-section p{
    font-size:12px !important;
    line-height:1.7 !important;
  }
  
  .intro-section li{
    font-size: 12px;
    line-height: 1.6;
  }

  /* TAB BUTTONS */
  .tab-navigation{
    padding:12px !important;
    gap:18px !important;
  }

  .tab-btn{
    width:100% !important;
    min-width:unset !important;
    font-size:13px !important;
    padding:8px 18px !important;
    border-radius:10px !important;
  }

  .tab-icon{
    font-size:12px !important;
    width:20px !important;
    height:20px !important;
  }

  /* SEARCH */
  .search-section,
  .state-selector-section{
    padding: 1rem;
  }

  .search-inner h2,
  .state-selector-inner h2{
    font-size: 20px;
  }

  .search-inner p,
  .state-selector-inner p{
    font-size: 16px !important;
    margin-bottom: 16px;
  }

  .search-row,
  .state-selector-row{
    flex-direction: column;
    align-items:center;
  }

  .search-row input{
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    font-size: 16px;
    padding: 14px;
  }
  .state-selector-row select{
    width:10% !important;
    max-width:50% !important;
    min-width:50% !important;

    height:42px !important;

    padding:6px 12px !important;

    font-size:13px !important;

    border-radius:8px !important;

    margin:0 auto !important;
    display:block;
  }

  .state-selector-row{
    justify-content:center !important;
  }
  .search-row button{
    width: 40%;
    font-size: 16px;
    padding: 14px;
  }

  /* FILTERS */
  .filter-bar{
    flex-wrap: nowrap;
    overflow-x: auto;
    justify-content: flex-start;
    padding: 12px;
    gap: 10px;
  }

  .filter-pill{
    flex: 0 0 auto;
    min-width: 110px;
    font-size: 13px;
    padding: 10px 16px;
  }

  /* RESULTS */
  .results-section{
    padding: 1rem 10px;
  }

  .race-group{
    margin-bottom: 20px;
  }

  .race-header{
    width: 100%;
    height: auto;
    padding: 14px;
    font-size: 14px;
    letter-spacing: 0.03em;
    border-radius: 12px;
  }

  .race-header-icon{
    font-size: 18px;
    margin-right: 8px;
  }

  /* CARDS */
  .candidate-card{
    width: 100%;
    padding: 12px;
    min-height: auto;
    gap: 12px;
    align-items: flex-start;
  }

  .candidate-name,
  .candidate-name-link{
    font-size: 18px;
    line-height: 1.3;
  }

  .candidate-website{
    word-break: break-word;
    font-size: 12px;
  }

  .candidate-meta{
    gap: 6px;
    margin-top: 10px;
  }

  .party-badge,
  .district-badge,
  .state-badge,
  .office-badge,
  .status-badge{
    font-size: 11px;
    padding: 5px 10px;
  }

  .candidate-date{
    font-size: 12px;
    margin-top: 8px;
  }

  .candidate-avatar{
    width: 50px;
    height: 50px;
    font-size: 16px;
  }

  .endorsement-seal,
  .endorsement-seal--yellow{
    width: 52px !important;
    height: 52px !important;
    max-width: 52px !important;
    max-height: 52px !important;
  }

  /* YOUNG VOTERS */
  .young-voters-wrapper{
    padding: 0 10px;
  }

  .young-voters-section{
    padding: 18px;
    margin-top: 25px;
    border-radius: 16px;
  }

  .young-voters-section h2{
    font-size: 16px;
    line-height: 1.4;
  }

  .young-voters-section p{
    font-size: 16px !important;
    line-height: 1.7;
  }

  .yv-step{
    margin-top: 22px;
  }

  .yv-step h3{
    font-size: 16px;
    margin-bottom: 10px;
  }

  .yv-step li{
    font-size: 14px;
    line-height: 1.6;
  }

  .yv-buttons{
    gap: 10px;
    
  }

  .yv-btn{
    width: 70%;
    font-size: 10px;
    padding: 8px;
    
  }

  .yv-notice{
    padding: 16px;
    font-size: 13px;
    line-height: 1.7;
  }

  .data-source{
    font-size: 10px;
  }

  /* REMOVE SIDE OVERFLOW */
  html,
  body{
    overflow-x: hidden;
  }
   .candidate-photo{
    width:60px;
    height:60px;
  }
}
      `}</style>

      

      <div className="hero">
        

        <h1>A4H VOTER GUIDE</h1>
        <div className="section-divider">
          <span>✦ ✦ ✦</span>
        </div>
        <div className="subtitle">
          Helping voters identify candidates
          aligned with Hindu and pro-Hindu values.
        </div>
        
      </div>

      {showGuide && (
        <div className="intro-section">
          <div className="intro-inner">
            <h3>About This Voter Guide</h3>

            <p>
              The Americans4Hindus Voter Guide helps Hindu American voters make 
              informed decisions in the 2026 elections. We provide comprehensive 
              candidate information across federal, state, and local races, along 
              with A4H endorsements for candidates who align with our community's values.
            </p>
            <h3>How to Use This Tool :</h3>
            <ul>
              <li>
                <strong>A4H Endorsed:</strong> View all endorsed
                candidates
              </li>

              <li>
                <strong>My State:</strong> Browse all candidates in
                your state
              </li>

              <li>
                <strong>My ZIP Code:</strong> Find candidates on
                your ballot
              </li>
            </ul>

            
          </div>
        </div>
      )}

      <div className="tab-navigation">
  <button
    className={`tab-btn ${
      activeTab === "endorsed"
        ? "active"
        : ""
    }`}
    onClick={() => {
      setActiveTab("endorsed");
      setActiveFilter("All");
    }}
  >
    <span className="tab-icon">★</span>
    A4H Endorsed
  </button>

  <button
    className={`tab-btn ${
      activeTab === "mystate"
        ? "active"
        : ""
    }`}
    onClick={() => {
      setActiveTab("mystate");
      setActiveFilter("All");
    }}
  >
    <span className="tab-icon">📍</span>
    My State
  </button>

  <button
    className={`tab-btn ${
      activeTab === "myzip"
        ? "active"
        : ""
    }`}
    onClick={() => {
      setActiveTab("myzip");
      setActiveFilter("All");
    }}
  >
    <span className="tab-icon zip-icon">⬚</span>
    My ZIP Code
  </button>
</div>

      {activeTab === "endorsed" && (
        <>
          <div className="search-section">
            <div className="search-inner">
              <h2>
                A4H Endorsed Candidates
              </h2>

              <p>
                View all candidates endorsed by
                Americans4Hindus.
              </p>
            </div>
          </div>

          <div className="filter-bar">
            {[
              "All",
              "Governor",
              "US Senate",
              "US House",
              "State Senate",
              "State House",
              "Local",
            ].map((f) => (
              <button
                key={f}
                className={`filter-pill ${
                  activeFilter === f
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  setActiveFilter(f)
                }
              >
                {f}
              </button>
            ))}
          </div>

          <div className="results-section">
            {renderCandidates(
              endorsedResults
            )}
          </div>
        </>
      )}

      {activeTab === "mystate" && (
        <>
          <div className="state-selector-section">
            <div className="state-selector-inner">
              <h2>Select Your State</h2>

              <p>
                Choose your state to view all
                candidates.
              </p>

              <div className="state-selector-row">
                <select
                  value={selectedState}
                  onChange={(e) =>
                    showStateResults(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    -- Select State --
                  </option>

                  {DATA.stateConfig.map((s) => (
                    <option
                      key={s.state}
                      value={s.state}
                    >
                      {s.stateName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {stateResults.length > 0 && (
            <>
              <div className="filter-bar">
                {[
                  "All",
                  "Governor",
                  "US Senate",
                  "US House",
                  "State Senate",
                  "State House",
                  "Local",
                ].map((f) => (
                  <button
                    key={f}
                    className={`filter-pill ${
                      activeFilter === f
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      setActiveFilter(f)
                    }
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="results-section">
                {renderCandidates(
                  stateResults
                )}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === "myzip" && (
        <>
          <div className="search-section">
            <div className="search-inner">
              <h2>Find Your Candidates</h2>

              <p>
                Enter your ZIP code to find your
                races.
              </p>

              <div className="search-row">
  <input
    type="text"
    value={zipInput}
    onChange={(e) =>
      setZipInput(
        e.target.value
      )
    }
    placeholder="Enter ZIP code"
    maxLength={5}
  />

  <button onClick={searchZip}>
    Search
  </button>
</div>


            </div>
          </div>

          {results.length > 0 && (
            <>
              <div className="filter-bar">
                {[
                  "All",
                  "Governor",
                  "US Senate",
                  "US House",
                  "State Senate",
                  "State House",
                  "Local",
                ].map((f) => (
                  <button
                    key={f}
                    className={`filter-pill ${
                      activeFilter === f
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      setActiveFilter(f)
                    }
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="results-section">
                {renderCandidates(results)}

                <div className="data-source">
                  Data last updated: May 11, 2026
                </div>
              </div>
            </>
          )}
          <div className="young-voters-wrapper">
  <div className="young-voters-section">
  <h2>
    YOUNG VOTERS (18+) – YOUR VOICE, YOUR VOTE, YOUR FUTURE
  </h2>

  <p>
    Turning 18 gives you the opportunity to participate in one 
    of the most important rights guaranteed by the United States 
    Constitution—the right to vote. Voting allows you to help shape 
    decisions affecting your community, education, economy, public 
    safety, and the future of our nation. Every election matters, including 
    local, state, and federal elections.
  </p>

  <div className="yv-step">
    <h3>STEP 1: VERIFY YOUR ELIGIBILITY</h3>

    <ul>
      <li style={{ listStyle: "none" }}><strong>Before registering, ensure that you:</strong></li>
      <li>Are a United States citizen.</li>
      <li>
        Are at least 18 years old on or before Election Day.
      </li>
      <li>Meet your state’s residency requirements.</li>
      <li>
        Are eligible to vote under your state’s election laws.
      </li>
    </ul>
  </div>

  <div className="yv-step">
    <h3>STEP 2: REGISTER TO VOTE</h3>

    <ul>
      <li style={{ listStyle: "none" }}><strong>Register as soon as you become eligible.</strong></li>
      <li>
        Complete your voter registration online, by mail,
        or in person.
      </li>

      <li>
        Keep your registration information current if you
        move or change your name.
      </li>

      <li>
        Check your voter registration status before each
        election.
      </li>
    </ul>
    <div className="yv-step">
    <h3>Official Registration Links</h3>
    <div className="yv-buttons">
      <a
        href="https://vote.gov"
        target="_blank"
        rel="noopener noreferrer"
        className="yv-btn"
      >
       National Voter Registration Portal
      </a>

      <a
        href="https://registertovote.ca.gov"
        target="_blank"
        rel="noopener noreferrer"
        className="yv-btn"
      >
       California Online Voter Registration
      </a>

      <a
        href="https://www.eac.gov/voters/national-mail-voter-registration-form"
        target="_blank"
        rel="noopener noreferrer"
        className="yv-btn"
      >
        National Mail Voter Registration Form
      </a>
    </div>
    </div>
  </div>

  <div className="yv-step">
    <h3>STEP 3: PREPARE BEFORE ELECTION DAY</h3>

    <ul>
      <li style={{ listStyle: "none" }}><strong>Become an informed voter.</strong></li>
      <li>Research candidates and ballot measures.</li>
      <li>Verify election dates and deadlines.</li>
      <li>
        Review voter identification requirements, if applicable
      </li>
      <li>
        Locate your polling place or vote-by-mail options.
      </li>
    </ul>
    
    <div className="yv-step">
    <h3>Official Registration Links</h3>
    <div className="yv-buttons">
      <a
        href="https://www.sos.ca.gov/elections"
        target="_blank"
        rel="noopener noreferrer"
        className="yv-btn"
      >
        California Elections Info
      </a>

      <a
        href="https://www.sos.ca.gov/elections/voter-registration"
        target="_blank"
        rel="noopener noreferrer"
        className="yv-btn"
      >
       California Voter Registration Info
      </a>
    </div>
    </div>
  </div>

  <div className="yv-step">
    <h3>STEP 4: CAST YOUR VOTE</h3>

    <ul>
      <li style={{ listStyle: "none" }}><strong>Choose the voting method available in your state.</strong></li>
      <li>Vote in person on Election Day.</li>
      <li>Vote during early voting if available.</li>
      <li>Vote by mail or absentee ballot if eligible</li>
      <li>
        Follow all instructions from election officials.
      </li>
    </ul>
  </div>

  <div className="yv-step">
    <h3>STEP 5: STAY ENGAGED</h3>

    <ul>
      <li style={{ listStyle: "none" }}><strong>Voting is not a one-time event.</strong></li>
      <li>
        Participate in local, state, and federal elections.
      </li>

      <li>
        Encourage family members and friends to vote.
      </li>

      <li>
        Stay informed through reliable and official election resources.
      </li>

      <li>
        Help strengthen civic participation within your community.
      </li>
    </ul>
  </div>
  <div className="yv-step">
    <h3>CHECK YOUR VOTER STATUS</h3>
  <div className="yv-buttons">
    <a
      href="https://voterstatus.sos.ca.gov"
      target="_blank"
      rel="noopener noreferrer"
      className="yv-btn status-btn"
    >
      California Voter Status Portal
    </a>
  </div>
  </div>


  <div className="yv-notice">
    IMPORTANT NOTICE: Americans4Hindus PAC encourages all eligible citizens to register, become informed, and participate in the democratic process. Voting eligibility, registration deadlines, identification requirements, and election procedures vary by state. Please consult your state or local election office for the most current information.

This information is provided for voter education and civic engagement purposes only and does not constitute legal advice or an endorsement of any candidate unless specifically stated elsewhere by Americans4Hindus PAC.
  </div>
  </div>
</div>
          

          
        </>
      )}
    </div>
  );
}
